import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const endPoint = process.env.REACT_APP_API_BASE_URL;

export default function QRAttendanceList() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // 🔍 Filters
  const [orgFilter, setOrgFilter] = useState("");
  const [markedByFilter, setMarkedByFilter] = useState("");
  const [dateFilter, setDateFilter] = useState("");

  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("qr_token");
    const role = localStorage.getItem("qr_role");

    if (!token || role !== "ADMIN") {
      if (!token) {
        navigate("/qr-attendance-login");
      } else {
        navigate("/qr-scanner");
      }
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await axios.post(
          `${endPoint}/api/all?page=${page}&size=10`,
          {},
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setRecords(res.data.content);
        setTotalPages(res.data.totalPages);
      } catch (err) {
        console.error(err);
        setError("⚠️ Failed to load records");
        if (err.response?.status === 401) {
          localStorage.removeItem("qr_token");
          localStorage.removeItem("qr_role");
          navigate("/qr-attendance-login");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [page, navigate]);

  const handleLogout = () => {
    localStorage.removeItem("qr_token");
    localStorage.removeItem("qr_token_expiry");
    localStorage.removeItem("qr_role");
    localStorage.removeItem("qr_user_name");
    navigate("/qr-attendance-login");
  };

  // ✅ Convert UTC → IST manually (+5:30)
  const toIST = (isoString) => {
    const date = new Date(isoString);
    date.setMinutes(date.getMinutes()); // removed IST Offset for prod
    return date;
  };

  // ✅ Format date for table (IST)
  const formatDate = (isoString) => {
    const date = toIST(isoString);
    return date.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  // ✅ Apply filters on records (IST bhi filter pe lagao)
  const filteredRecords = records.filter((item) => {
    const matchesOrg =
      !orgFilter ||
      item.organisationDetail?.toLowerCase().includes(orgFilter.toLowerCase());

    const matchesMarkedBy =
      !markedByFilter ||
      item.markedBy?.toLowerCase().includes(markedByFilter.toLowerCase());

    let matchesDate = true;
    if (dateFilter) {
      const istDate = toIST(item.createdAt);
      const formattedDate = istDate.toLocaleDateString("en-CA"); // yyyy-mm-dd (IST)
      matchesDate = formattedDate === dateFilter;
    }

    return matchesOrg && matchesMarkedBy && matchesDate;
  });

  const renderTable = () => {
    if (loading) return <p>⏳ Loading...</p>;
    if (error) return <p style={{ color: "red" }}>{error}</p>;
    if (filteredRecords.length === 0) return <p>⚠️ No records found</p>;

    return (
      <div style={{ overflowX: "auto" }}>
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            background: "#fff",
            borderRadius: "8px",
            overflow: "hidden",
            boxShadow: "0 1px 6px rgba(0,0,0,0.1)",
          }}
        >
          <thead>
            <tr style={{ background: "#f1f2f6", color: "#2c3e50" }}>
              <th style={{ padding: "10px", textAlign: "left" }}>#</th>
              <th style={{ padding: "10px", textAlign: "left" }}>Name</th>
              <th style={{ padding: "10px", textAlign: "left" }}>Mobile</th>
              <th style={{ padding: "10px", textAlign: "left" }}>
                Organisation
              </th>
              <th style={{ padding: "10px", textAlign: "left" }}>Marked By</th>
              <th style={{ padding: "10px", textAlign: "left" }}>
                Attendance Time (IST)
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredRecords.map((item, index) => (
              <tr
                key={item.id}
                style={{
                  borderBottom: "1px solid #eee",
                  background: index % 2 === 0 ? "#fff" : "#fafafa",
                }}
              >
                <td style={{ padding: "10px" }}>{page * 10 + index + 1}</td>
                <td style={{ padding: "10px" }}>{item.name}</td>
                <td style={{ padding: "10px" }}>{item.mobileNumber}</td>
                <td style={{ padding: "10px" }}>{item.organisationDetail}</td>
                <td style={{ padding: "10px" }}>{item.markedBy}</td>
                <td style={{ padding: "10px" }}>
                  {formatDate(item.createdAt)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div
      style={{
        background: "#f9fafb",
        color: "#111",
        minHeight: "100vh",
        padding: "20px",
        fontFamily: "monospace",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          flexWrap: "wrap",
          alignItems: "center",
          marginBottom: "20px",
          gap: "10px",
        }}
      >
        <h2 style={{ margin: 0, fontSize: "1.5rem", fontWeight: "bold" }}>
          📋 QR Attendance List
        </h2>

        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
          <button
            type="button"
            onClick={() => navigate("/Qrscanner")}
            style={{
              background: "#3b82f6",
              border: "none",
              borderRadius: "8px",
              padding: "10px 15px",
              color: "#fff",
              cursor: "pointer",
              fontWeight: "bold",
              minWidth: "130px",
            }}
          >
            📷 Open Scanner
          </button>
          <button
            type="button"
            onClick={handleLogout}
            style={{
              background: "#ef4444",
              border: "none",
              borderRadius: "8px",
              padding: "10px 15px",
              color: "#fff",
              cursor: "pointer",
              fontWeight: "bold",
              minWidth: "100px",
            }}
          >
            🚪 Logout
          </button>
        </div>
      </div>

      {/* 🔍 Filter Section */}
      <div
        style={{
          display: "flex",
          gap: "10px",
          marginBottom: "20px",
          flexWrap: "wrap",
        }}
      >
        <input
          type="text"
          placeholder="Filter by Organisation"
          value={orgFilter}
          onChange={(e) => setOrgFilter(e.target.value)}
          style={{
            padding: "10px",
            borderRadius: "8px",
            border: "1px solid #ccc",
            flex: 1,
          }}
        />
        <input
          type="text"
          placeholder="Filter by Marked By"
          value={markedByFilter}
          onChange={(e) => setMarkedByFilter(e.target.value)}
          style={{
            padding: "10px",
            borderRadius: "8px",
            border: "1px solid #ccc",
            flex: 1,
          }}
        />
        <input
          type="date"
          value={dateFilter}
          onChange={(e) => setDateFilter(e.target.value)}
          style={{
            padding: "10px",
            borderRadius: "8px",
            border: "1px solid #ccc",
          }}
        />
      </div>

      {/* Table */}
      {renderTable()}

      {/* Pagination */}
      <div
        style={{
          marginTop: "20px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: "10px",
          flexWrap: "wrap",
        }}
      >
        <button
          type="button"
          onClick={() => setPage((p) => Math.max(p - 1, 0))}
          disabled={page === 0}
          style={{
            padding: "8px 14px",
            borderRadius: "8px",
            border: "none",
            cursor: page === 0 ? "not-allowed" : "pointer",
            background: page === 0 ? "#bdc3c7" : "#3b82f6",
            color: "#fff",
            fontWeight: "bold",
            minWidth: "100px",
          }}
        >
          ⬅ Prev
        </button>
        <span>
          Page {page + 1} of {totalPages}
        </span>
        <button
          type="button"
          onClick={() => setPage((p) => (p < totalPages - 1 ? p + 1 : p))}
          disabled={page === totalPages - 1}
          style={{
            padding: "8px 14px",
            borderRadius: "8px",
            border: "none",
            cursor: page === totalPages - 1 ? "not-allowed" : "pointer",
            background: page === totalPages - 1 ? "#bdc3c7" : "#3b82f6",
            color: "#fff",
            fontWeight: "bold",
            minWidth: "100px",
          }}
        >
          Next ➡
        </button>
      </div>
    </div>
  );
}
